"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function AdminPanel() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: benefitsRef,
    isVisible: benefitsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const domains = [{
    number: "01",
    label: "RECEPCIÓN",
    title: "Recepción",
    description: "Gestión centralizada de solicitudes de transporte y asignación inmediata de conductores para minimizar tiempos de espera.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/reception_management_interface.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/reception_management_interface.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/reception_management_interface.png-wvc-sizes",
    alt: "Interfaz de gestión de recepción",
    aspect: "aspect-[4/5]"
  }, {
    number: "02",
    label: "GUEST SERVICES",
    title: "Guest Services",
    description: "Atención personalizada al huésped con visibilidad total sobre la disponibilidad de la flota en todo el complejo.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/guest_services_dashboard.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/guest_services_dashboard.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/guest_services_dashboard.png-wvc-sizes",
    alt: "Dashboard de servicios al huésped",
    aspect: "aspect-[4/5]"
  }, {
    number: "03",
    label: "RESTAURACIÓN",
    title: "Restauración",
    description: "Coordinación logística para traslados a puntos de consumo, optimizando el flujo de comensales entre áreas.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/restaurant_logistics_view.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/restaurant_logistics_view.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/restaurant_logistics_view.png-wvc-sizes",
    alt: "Vista de logística de restauración",
    aspect: "aspect-[4/5]"
  }, {
    number: "04",
    label: "OPERACIONES",
    title: "Operaciones",
    description: "Control técnico y mantenimiento preventivo de la flota basado en datos de uso real y telemetría avanzada.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/operations_fleet_control.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/operations_fleet_control.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701638/img/operations_fleet_control.webp-wvc-sizes",
    alt: "Control de flota de operaciones",
    aspect: "aspect-[4/5]"
  }];
  return <section data-section-id="3035" id="admin-panel" className="relative bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-y-8 mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-start-2 lg:col-span-10 flex items-center gap-3 mb-4">
            <span className="inline-block w-2 h-2 rounded-md bg-primary" />
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
              Información que ayuda a tomar mejores decisiones.
            </span>
          </div>
          <div className="lg:col-start-2 lg:col-span-8">
            <h2 className="font-default text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground">
              Operations Intelligence
            </h2>
            <p className="font-default text-lg text-muted-foreground mt-4 max-w-2xl">
              Una plataforma integral diseñada para conectar cada departamento de su resort con la movilidad inteligente en tiempo real.
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 transition-all duration-1000 ease-out ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {domains.map((item, index) => <div key={index} data-index={index}>
              <CardItem item={item} />
            </div>)}
        </div>

        <div ref={benefitsRef} className={`mt-24 md:mt-32 max-w-3xl mx-auto transition-all duration-1000 ease-out delay-300 ${benefitsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h3 className="font-default text-2xl md:text-3xl text-foreground mb-6">Beneficios</h3>
          <ul className="list-disc list-inside space-y-3 text-muted-foreground text-lg">
            <li>Coordinación: Sincronización perfecta entre departamentos para una respuesta ágil.</li>
            <li>Optimización: Reducción de tiempos muertos y maximización del uso de recursos.</li>
            <li>Mejor servicio: Una experiencia de huésped fluida, eliminando la incertidumbre en los traslados.</li>
          </ul>
        </div>
      </div>
    </section>;
}
function CardItem({
  item
}: {
  item: {
    number: string;
    label: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    aspect: string;
  };
}) {
  return <div className="block transition-transform duration-700 ease-out hover:-translate-y-1.5 group">
      <div className={`relative w-full ${item.aspect} overflow-hidden rounded-lg bg-muted shadow-md`}>
        <img src={item.image} data-wvc-srcset={item.srcset} data-wvc-sizes={item.sizes} alt={item.alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.03]" />
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <span className="font-default text-2xl text-primary leading-none">
          {item.number}
        </span>
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground">
          {item.label}
        </span>
        <h3 className="font-default text-2xl leading-tight tracking-tight text-foreground">
          {item.title}
        </h3>
        <p className="font-default text-sm leading-relaxed text-muted-foreground mt-1">
          {item.description}
        </p>
      </div>
    </div>;
}